import { TicketFormState } from '../types'

const toBase64 = (data: string): string => {
  return btoa(data)
}

const BASIC = `Basic ${toBase64(
  `${import.meta.env.VITE_APP_TEAMWORK_API_KEY}:password`
)}`

const SITE_URL = import.meta.env.VITE_APP_TEAMWORK_SITE

const authHeaders = () => ({
  Authorization: BASIC
})

const jsonHeaders = () => ({
  ...authHeaders(),
  'Content-Type': 'application/json'
})

const getUserIdByEmail = async (email: string): Promise<number | null> => {
  const url = `https://${SITE_URL}/people.json?emailAddress=${encodeURIComponent(
    email
  )}`
  const response = await fetch(url, { headers: authHeaders() })
  const json = await response.json()

  const id = json?.people?.length > 0 ? json?.people[0]?.id : null

  return id ? Number(id) : null
}

const getDescription = (payload: TicketFormState) => {
  const url = payload.url ?? window?.location?.href
  const env = payload.environment ?? import.meta.env.VITE_APP_ENV

  const userLine = payload.user
    ? `**User:** ${payload?.user?.user?.first_name} ${payload?.user?.user?.last_name}(<${payload?.user?.user?.email}>)`
    : '**User:** (not provided)'

  const lines: string[] = [
    `**Description:** ${payload.description?.trim() ?? ''}`,
    '',
    `**Site URL:**: ${url}`,
    userLine,
    `**Environment:** ${env}`
  ]

  return lines.join('\n')
}

export const createSubtask = async (payload: TicketFormState) => {
  const taskData = `https://${SITE_URL}/projects/api/v3/tasklists/${
    import.meta.env.VITE_APP_PARENT_TASK_ID
  }/tasks.json`

  const userId = payload.user?.user?.email
    ? await getUserIdByEmail(payload?.user?.user?.email)
    : null

  const hasDefaultAssignee = import.meta.env.VITE_APP_TICKETS_DEFAULT_ASSIGNEE

  const defaultAssigneeId = hasDefaultAssignee
    ? await getUserIdByEmail(hasDefaultAssignee)
    : null

  const response = await fetch(taskData, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      task: {
        name: payload.title,
        description: getDescription(payload),
        priority: payload?.priority,
        ...(hasDefaultAssignee && {
          assignees: {
            userIds: [defaultAssigneeId]
          }
        }),
        ...(userId && {
          completeFollowers: {
            userIds: [userId]
          },
          changeFollowers: {
            userIds: [userId]
          },
          commentFollowers: {
            userIds: [userId]
          },
          notify: true
        })
      }
    })
  })
  const json = await response.json()

  const id = json?.task?.id
  if (!id) throw new Error('Teamwork did not return a new task id')
  return id
}

const getPresignedUrl = async (fileName: string, fileSize: number) => {
  const url = new URL(
    `https://${SITE_URL}/projects/api/v1/pendingfiles/presignedurl.json`
  )
  url.searchParams.set('fileName', fileName)
  url.searchParams.set('fileSize', String(fileSize))

  const response = await fetch(url.toString(), { headers: authHeaders() })

  const json = await response.json()

  return json
}

const uploadFileToUrl = async (uploadUrl: string, file: File) => {
  await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'x-amz-acl': 'public-read'
    },
    body: file
  })
}

const attachPendingRefs = async (
  taskId: number,
  refs: string[]
): Promise<void> => {
  if (!refs.length) return
  const files = `https://${SITE_URL}/tasks/${taskId}/files.json`

  await fetch(files, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      task: {
        pendingFileAttachments: refs,
        updateFiles: true,
        removeOtherFiles: false
      }
    })
  })
}

export const submitFormToTeamworkBasic = async (payload: TicketFormState) => {
  const taskId = await createSubtask(payload)

  const files = (payload.attachments ?? []).map((a) => a?.originFileObj)

  if (files?.length) {
    const refs: string[] = []
    for (const f of files) {
      const { ref, url } = await getPresignedUrl(f.name, f.size)

      await uploadFileToUrl(url, f)
      refs.push(ref)
    }

    await attachPendingRefs(taskId, refs)
  }

  return { taskId }
}
